import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Eye, Clock, X, ChevronRight, ChevronLeft, Check, AlertCircle, Minus, Plus } from 'lucide-react';
import axios from 'axios';

/**
 * ProductServiceListing - Renders a grid of products, services, or boarding spaces
 * Styled to match OrderOnline page cards and Home page section titles
 */
const ProductServiceListing = ({ config, onAddToCart }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(null);

  // Image preview state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Bargain modal state
  const [showBargainModal, setShowBargainModal] = useState(false);
  const [bargainProduct, setBargainProduct] = useState(null);
  const [bargainForm, setBargainForm] = useState({
    customerQuote: '',
    quantity: 1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [bargainSubmitting, setBargainSubmitting] = useState(false);
  const [bargainError, setBargainError] = useState('');
  const [bargainSuccess, setBargainSuccess] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const {
    listing_type = '',
    filter_mode = 'category',
    section_title = '',
    section_subtitle = '',
    items_count = 6,
    grid_columns = 2,
    selected_menu_category = '',
    selected_categories = [],
    show_view_all_button = true,
    view_all_text = 'View All',
    view_all_link = '',
    show_price = true,
    show_description = true,
    show_add_to_cart = true,
    show_stock_status = true,
    show_duration = true,
    enable_modal = true
  } = config || {};

  useEffect(() => {
    if (listing_type) {
      fetchItems();
    } else {
      setLoading(false);
      setItems([]);
    }
  }, [listing_type, filter_mode, selected_menu_category, selected_categories, items_count]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        listing_type: listing_type,
        filter_mode: filter_mode,
        limit: items_count.toString()
      });
      
      if (filter_mode !== 'component_enabled') {
        if (selected_menu_category) {
          params.append('menu_category', selected_menu_category);
        }
        if (selected_categories.length > 0) {
          params.append('categories', selected_categories.join(','));
        }
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/product-listing/items?${params}`);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    if (enable_modal) {
      setSelectedItem(item);
      setQuantity(1);
      setShowModal(true);
    }
  };

  const handleAddToCart = async (item, qty = 1, e) => {
    if (e) e.stopPropagation();
    setAddingToCart(item.id);
    
    // Simulate brief loading
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (onAddToCart) {
      onAddToCart(item, qty);
    } else {
      window.dispatchEvent(new CustomEvent('addToCart', { detail: { item, quantity: qty } }));
    }
    
    setAddingToCart(null);
  };

  // Image preview functions
  const openImagePreview = (item, startIndex = 0, e) => {
    if (e) e.stopPropagation();
    const allMedia = [];
    
    if (item.image_url || item.photo_url) {
      allMedia.push({ url: item.image_url || item.photo_url, type: 'image' });
    }
    
    if (item.media_gallery && item.media_gallery.length > 0) {
      item.media_gallery.forEach(m => allMedia.push({ url: m.url, type: m.type || 'image' }));
    }
    
    if (allMedia.length === 0) return;
    
    setPreviewImages(allMedia);
    setPreviewImageIndex(startIndex);
    setShowImagePreview(true);
  };

  const prevPreviewImage = (e) => {
    e.stopPropagation();
    if (previewImages.length > 1) {
      const newIndex = previewImageIndex === 0 ? previewImages.length - 1 : previewImageIndex - 1;
      setPreviewImageIndex(newIndex);
    }
  };

  const nextPreviewImage = (e) => {
    e.stopPropagation();
    if (previewImages.length > 1) {
      const newIndex = previewImageIndex === previewImages.length - 1 ? 0 : previewImageIndex + 1;
      setPreviewImageIndex(newIndex);
    }
  };

  const zoomIn = (e) => {
    e.stopPropagation();
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = (e) => {
    e.stopPropagation();
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = (e) => {
    e.stopPropagation();
    setImageZoom(1);
  };

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!isFullscreen) {
      const container = document.getElementById('image-preview-container-listing');
      if (container?.requestFullscreen) {
        container.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const closeImagePreview = () => {
    if (isFullscreen && document.exitFullscreen) {
      document.exitFullscreen();
    }
    setShowImagePreview(false);
    setImageZoom(1);
    setIsFullscreen(false);
  };

  // Bargain functions
  const openBargainModal = (item, e) => {
    if (e) e.stopPropagation();
    setBargainProduct(item);
    setBargainForm({
      customerQuote: '',
      quantity: 1,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      notes: ''
    });
    setBargainError('');
    setBargainSuccess(false);
    setShowBargainModal(true);
  };

  const submitBargain = async () => {
    if (!bargainForm.customerQuote || parseFloat(bargainForm.customerQuote) <= 0) {
      setBargainError('Please enter a valid price offer');
      return;
    }
    if (!bargainForm.firstName || !bargainForm.lastName) {
      setBargainError('Please enter your name');
      return;
    }
    if (!bargainForm.email) {
      setBargainError('Please enter your email');
      return;
    }
    if (!bargainForm.phone) {
      setBargainError('Please enter your phone number');
      return;
    }

    setBargainSubmitting(true);
    setBargainError('');

    try {
      await axios.post(`${API_BASE_URL}/api/bargain-requests`, {
        product_id: bargainProduct.id,
        product_name: bargainProduct.name,
        original_price: bargainProduct.price,
        customer_quote: parseFloat(bargainForm.customerQuote),
        quantity: bargainForm.quantity,
        customer_first_name: bargainForm.firstName,
        customer_last_name: bargainForm.lastName,
        customer_email: bargainForm.email,
        customer_phone: bargainForm.phone,
        notes: bargainForm.notes
      });

      setBargainSuccess(true);
      setTimeout(() => {
        setShowBargainModal(false);
        setBargainSuccess(false);
      }, 2000);
    } catch (err) {
      setBargainError(err.response?.data?.detail || 'Failed to submit bargain request');
    } finally {
      setBargainSubmitting(false);
    }
  };

  const getMediaCount = (item) => {
    let count = 0;
    if (item.image_url || item.photo_url) count++;
    if (item.media_gallery?.length) count += item.media_gallery.length;
    return count;
  };

  const getDefaultLink = () => {
    if (view_all_link) return view_all_link;
    switch (listing_type) {
      case 'products': return '/order-online';
      case 'services': return '/book-appointment';
      case 'boarding': return '/book-boarding';
      default: return '/';
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Don't render if no listing type selected
  if (!listing_type) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {section_title && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center">
                <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
                <div className="mx-4 px-6 py-2 bg-white rounded-full border border-gray-200 shadow-sm">
                  <h2 className="font-bold text-gray-900 text-lg tracking-wide">{section_title}</h2>
                </div>
                <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
              </div>
            </div>
          )}
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-blue-500 rounded-full"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-500 py-8">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header - Styled like Home page */}
          {(section_title || section_subtitle) && (
            <div className="text-center mb-8">
              {section_title && (
                <div className="inline-flex items-center justify-center mb-4">
                  <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
                  <div className="mx-4 px-6 py-2 bg-white rounded-full border border-gray-200 shadow-sm">
                    <h2 className="font-bold text-gray-900 text-lg tracking-wide">{section_title}</h2>
                  </div>
                  <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
                </div>
              )}
              {section_subtitle && (
                <p className="text-gray-600 max-w-3xl mx-auto" style={{ fontSize: '1rem' }}>
                  {section_subtitle}
                </p>
              )}
            </div>
          )}

          {/* Items Grid - 2 columns, horizontal cards like Order Online */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg flex ${
                  enable_modal ? 'cursor-pointer' : ''
                }`}
              >
                {/* Image - Left Side with hover preview */}
                <div className="w-40 h-40 flex-shrink-0 bg-gray-50 relative group">
                  {(item.image_url || item.photo_url) ? (
                    <>
                      <img
                        src={item.image_url || item.photo_url}
                        alt={item.name || item.service_name || item.space_name}
                        className="w-full h-full object-contain"
                      />
                      {/* Hover Preview Button */}
                      <button
                        onClick={(e) => openImagePreview(item, 0, e)}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        type="button"
                      >
                        <Eye className="h-8 w-8 text-white" />
                      </button>
                      {/* Media Count Badge */}
                      {getMediaCount(item) > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {getMediaCount(item)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  )}
                  
                  {/* Stock Badge for Products */}
                  {listing_type === 'products' && show_stock_status && item.in_stock === false && (
                    <div className="absolute top-2 left-2 px-2 py-1 text-xs font-medium rounded bg-red-600 text-white">
                      Out of Stock
                    </div>
                  )}
                  
                  {/* Duration Badge for Services */}
                  {listing_type === 'services' && show_duration && (item.duration_minutes || item.duration) && (
                    <div className="absolute top-2 left-2 px-2 py-1 text-xs font-medium rounded bg-gray-800 text-white flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(item.duration_minutes || item.duration)}
                    </div>
                  )}
                </div>

                {/* Content - Right Side */}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">
                      {item.name || item.service_name || item.space_name}
                    </h3>
                    
                    {show_description && item.description && (
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Price and Action Row */}
                  <div className="flex items-center justify-between mt-2">
                    {show_price && (item.price || item.price === 0) && (
                      <span className="text-sm font-semibold text-gray-900">
                        {formatPrice(item.price)}
                      </span>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-1">
                      {/* Bargain Button */}
                      {listing_type === 'products' && item.bargain_enabled && (
                        <button
                          onClick={(e) => openBargainModal(item, e)}
                          className="text-white px-2 py-1.5 rounded-lg hover:opacity-90 transition-colors text-xs"
                          style={{ backgroundColor: 'rgb(234, 88, 12)' }}
                          title="Make a price offer"
                        >
                          💰 Bargain
                        </button>
                      )}
                      
                      {/* Add Button */}
                      {listing_type === 'products' && show_add_to_cart && item.in_stock !== false && (
                        <button
                          onClick={(e) => handleAddToCart(item, 1, e)}
                          disabled={addingToCart === item.id}
                          className="text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-colors flex items-center gap-1 text-sm disabled:opacity-70"
                          style={{ backgroundColor: 'var(--theme-primary)' }}
                        >
                          {addingToCart === item.id ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Adding
                            </>
                          ) : (
                            <>
                              <Plus className="h-3 w-3" />
                              Add
                            </>
                          )}
                        </button>
                      )}
                      
                      {/* Book Now for Services/Boarding */}
                      {listing_type === 'services' && (
                        <Link
                          to="/book-appointment"
                          onClick={(e) => e.stopPropagation()}
                          className="text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-colors text-sm"
                          style={{ backgroundColor: 'var(--theme-primary)' }}
                        >
                          Book
                        </Link>
                      )}
                      
                      {listing_type === 'boarding' && (
                        <Link
                          to="/book-boarding"
                          onClick={(e) => e.stopPropagation()}
                          className="text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-colors text-sm"
                          style={{ backgroundColor: 'var(--theme-primary)' }}
                        >
                          Book
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Button */}
          {show_view_all_button && (
            <div className="text-center mt-8">
              <Link
                to={getDefaultLink()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                {view_all_text}
                <ChevronRight className="h-5 w-5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Item Detail Modal - Styled like Order Online customization modal */}
      {showModal && selectedItem && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="flex-shrink-0 px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              <h3 className="text-lg font-semibold text-white">
                {selectedItem.name || selectedItem.service_name || selectedItem.space_name}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Product Image and Description */}
              <div className="flex gap-4">
                <div className="relative w-40 h-40 flex-shrink-0 bg-gray-50 rounded-lg group">
                  {(selectedItem.image_url || selectedItem.photo_url) ? (
                    <>
                      <img
                        src={selectedItem.image_url || selectedItem.photo_url}
                        alt={selectedItem.name || selectedItem.service_name || selectedItem.space_name}
                        className="w-full h-full object-contain rounded-lg"
                      />
                      <button
                        onClick={(e) => openImagePreview(selectedItem, 0, e)}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        type="button"
                      >
                        <Eye className="h-8 w-8 text-white" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-5xl">
                        {listing_type === 'products' ? '📦' : 
                         listing_type === 'services' ? '🛠️' : '🏠'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {selectedItem.category && (
                    <span className="inline-block mb-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {selectedItem.category}
                    </span>
                  )}
                  {selectedItem.description && (
                    <p className="text-sm text-gray-600 mb-2 line-clamp-4">{selectedItem.description}</p>
                  )}
                  {show_price && (selectedItem.price || selectedItem.price === 0) && (
                    <p className="text-xl font-bold text-gray-900">
                      {formatPrice(selectedItem.price)}
                    </p>
                  )}
                </div>
              </div>

              {/* Product Variants */}
              {listing_type === 'products' && selectedItem.is_variable && selectedItem.variants?.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Available Options</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedItem.variants.map((variant, idx) => (
                      <div key={idx} className="p-2 rounded-md border border-gray-300 bg-white">
                        <div className="text-sm font-medium text-gray-900">{variant.name}</div>
                        <div className="text-sm text-gray-600">{formatPrice(variant.price)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Service Duration */}
              {listing_type === 'services' && (selectedItem.duration_minutes || selectedItem.duration) && (
                <div className="flex items-center gap-2 text-gray-600 bg-gray-50 rounded-lg p-3">
                  <Clock className="h-5 w-5" />
                  <span>Duration: {formatDuration(selectedItem.duration_minutes || selectedItem.duration)}</span>
                </div>
              )}

              {/* Stock Status */}
              {listing_type === 'products' && show_stock_status && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${
                  selectedItem.in_stock !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {selectedItem.in_stock !== false ? (
                    <>
                      <Check className="h-5 w-5" />
                      <span>In Stock</span>
                      {selectedItem.quantity && (
                        <span className="text-gray-500 ml-2">({selectedItem.quantity} available)</span>
                      )}
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5" />
                      <span>Out of Stock</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                {/* Quantity Selector for Products */}
                {listing_type === 'products' && selectedItem.in_stock !== false && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
                    >
                      <Minus className="h-4 w-4 text-gray-700" />
                    </button>
                    <span className="text-base font-semibold text-gray-900 min-w-[35px] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
                    >
                      <Plus className="h-4 w-4 text-gray-700" />
                    </button>
                  </div>
                )}
                
                {/* Total and Action Buttons */}
                <div className="flex items-center gap-3 ml-auto">
                  {listing_type === 'products' && selectedItem.in_stock !== false && show_price && (
                    <div className="text-base font-bold text-gray-900">
                      {formatPrice((selectedItem.price || 0) * quantity)}
                    </div>
                  )}

                  {/* Bargain Button in Modal */}
                  {listing_type === 'products' && selectedItem.bargain_enabled && (
                    <button
                      onClick={(e) => {
                        setShowModal(false);
                        openBargainModal(selectedItem, e);
                      }}
                      className="px-3 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-colors"
                      style={{ backgroundColor: 'rgb(234, 88, 12)' }}
                    >
                      💰 Bargain
                    </button>
                  )}
                  
                  {listing_type === 'products' && selectedItem.in_stock !== false && (
                    <button
                      onClick={() => {
                        handleAddToCart(selectedItem, quantity);
                        setShowModal(false);
                      }}
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-colors flex items-center gap-1.5"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      <Plus className="h-4 w-4" />
                      Add to Cart
                    </button>
                  )}
                  
                  {listing_type === 'services' && (
                    <Link
                      to="/book-appointment"
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-colors"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                      onClick={() => setShowModal(false)}
                    >
                      Book Now
                    </Link>
                  )}
                  
                  {listing_type === 'boarding' && (
                    <Link
                      to="/book-boarding"
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-colors"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                      onClick={() => setShowModal(false)}
                    >
                      Book Now
                    </Link>
                  )}

                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Preview Modal */}
      {showImagePreview && ReactDOM.createPortal(
        <div 
          id="image-preview-container-listing"
          className={`fixed inset-0 bg-black ${isFullscreen ? '' : 'bg-opacity-90'} flex items-center justify-center z-[60]`}
          onClick={closeImagePreview}
        >
          <div 
            className={`relative flex items-center justify-center ${
              isFullscreen ? 'w-screen h-screen' : 'max-w-[90vw] max-h-[85vh] rounded-lg'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Current Image/Video */}
            {(() => {
              const currentMedia = previewImages[previewImageIndex];
              const isVideo = currentMedia?.type === 'video';
              const url = currentMedia?.url;
              
              if (isVideo) {
                return (
                  <div className={`${isFullscreen ? 'w-[95vw] h-[95vh]' : 'w-[85vw] max-w-4xl'} aspect-video bg-black rounded-lg overflow-hidden`}>
                    <video
                      src={url}
                      className="w-full h-full"
                      controls
                      autoPlay
                      playsInline
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                );
              } else {
                return (
                  <div 
                    className="overflow-auto"
                    style={{ 
                      maxWidth: isFullscreen ? '98vw' : '85vw',
                      maxHeight: isFullscreen ? '98vh' : '80vh'
                    }}
                  >
                    <img
                      src={url}
                      alt="Preview"
                      className="select-none block"
                      style={{ 
                        width: imageZoom > 1 ? `calc(${isFullscreen ? '98vw' : '85vw'} * ${imageZoom})` : 'auto',
                        height: imageZoom > 1 ? `calc(${isFullscreen ? '98vh' : '80vh'} * ${imageZoom})` : 'auto',
                        maxWidth: imageZoom > 1 ? 'none' : (isFullscreen ? '98vw' : '85vw'),
                        maxHeight: imageZoom > 1 ? 'none' : (isFullscreen ? '98vh' : '80vh'),
                        objectFit: 'contain'
                      }}
                      draggable={false}
                    />
                  </div>
                );
              }
            })()}
            
            {/* Top Right Controls */}
            <div className="absolute top-2 right-2 flex items-center gap-1 z-20">
              {/* Zoom Controls */}
              {previewImages[previewImageIndex]?.type !== 'video' && (
                <div className="flex items-center bg-black bg-opacity-50 rounded-full px-1 py-0.5">
                  <button
                    onClick={zoomOut}
                    disabled={imageZoom <= 0.5}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all disabled:opacity-40"
                    type="button"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <button
                    onClick={resetZoom}
                    className="px-1.5 py-0.5 text-white text-xs font-medium hover:bg-white hover:bg-opacity-20 rounded min-w-[36px]"
                    type="button"
                  >
                    {Math.round(imageZoom * 100)}%
                  </button>
                  <button
                    onClick={zoomIn}
                    disabled={imageZoom >= 3}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all disabled:opacity-40"
                    type="button"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Fullscreen Button */}
              <button
                onClick={toggleFullscreen}
                className="p-1.5 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all"
                type="button"
              >
                {isFullscreen ? (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                )}
              </button>
              
              {/* Close Button */}
              <button
                onClick={closeImagePreview}
                className="p-1.5 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all"
                type="button"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            
            {/* Bottom Navigation */}
            {previewImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
                <button
                  onClick={prevPreviewImage}
                  className="p-1.5 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all"
                  type="button"
                >
                  <ChevronLeft className="h-5 w-5 text-white" />
                </button>
                
                <div className="bg-black bg-opacity-50 rounded-full px-3 py-1">
                  <span className="text-white text-xs font-medium">
                    {previewImageIndex + 1} / {previewImages.length}
                  </span>
                </div>
                
                <button
                  onClick={nextPreviewImage}
                  className="p-1.5 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all"
                  type="button"
                >
                  <ChevronRight className="h-5 w-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Bargain Modal */}
      {showBargainModal && bargainProduct && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowBargainModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div 
              className="flex-shrink-0 px-4 py-3 flex items-center justify-between rounded-t-xl"
              style={{ backgroundColor: 'rgb(234, 88, 12)' }}
            >
              <h3 className="text-lg font-semibold text-white">💰 Make an Offer</h3>
              <button
                onClick={() => setShowBargainModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {bargainSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Offer Submitted!</h4>
                  <p className="text-gray-600">We'll review your offer and get back to you soon.</p>
                </div>
              ) : (
                <>
                  {/* Product Info */}
                  <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    {(bargainProduct.image_url || bargainProduct.photo_url) && (
                      <img
                        src={bargainProduct.image_url || bargainProduct.photo_url}
                        alt={bargainProduct.name}
                        className="w-16 h-16 object-contain rounded-lg"
                      />
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900">{bargainProduct.name}</h4>
                      <p className="text-sm text-gray-600">Original: {formatPrice(bargainProduct.price)}</p>
                      {bargainProduct.min_bargain_price && (
                        <p className="text-xs text-orange-600">Min: {formatPrice(bargainProduct.min_bargain_price)}</p>
                      )}
                    </div>
                  </div>

                  {bargainError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {bargainError}
                    </div>
                  )}

                  {/* Price Offer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Price Offer *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={bargainForm.customerQuote}
                        onChange={(e) => setBargainForm({ ...bargainForm, customerQuote: e.target.value })}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={bargainForm.quantity}
                      onChange={(e) => setBargainForm({ ...bargainForm, quantity: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={bargainForm.firstName}
                        onChange={(e) => setBargainForm({ ...bargainForm, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={bargainForm.lastName}
                        onChange={(e) => setBargainForm({ ...bargainForm, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={bargainForm.email}
                      onChange={(e) => setBargainForm({ ...bargainForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={bargainForm.phone}
                      onChange={(e) => setBargainForm({ ...bargainForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                    <textarea
                      value={bargainForm.notes}
                      onChange={(e) => setBargainForm({ ...bargainForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {!bargainSuccess && (
              <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => setShowBargainModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={submitBargain}
                  disabled={bargainSubmitting}
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: 'rgb(234, 88, 12)' }}
                >
                  {bargainSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    'Submit Offer'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ProductServiceListing;
